import { Suspense } from 'react';

import { useAddContractContext } from '@/contexts/add-contract-context';

import { ChannelDetails } from './channel-details';
import { ChannelSearchSection } from './channel-search-section';
import { ContractFormFooter } from './contract-form-footer';
import { Success } from './success';
import type { ContractSubmitSuccess } from './types';
import { useContractForm } from './use-contract-form';
import { Separator } from '@/components/ui/separator';

export type { ContractSubmitSuccess };

interface ChannelContentProps {
  submitSuccess?: ContractSubmitSuccess | null;
  onSubmitSuccess: (result: ContractSubmitSuccess) => void;
  onClose: () => void;
}

export function ChannelContent({
  submitSuccess = null,
  onSubmitSuccess,
  onClose,
}: ChannelContentProps) {
  const { channelId } = useAddContractContext();

  if (submitSuccess) {
    return (
      <ContractSuccessBody submitSuccess={submitSuccess} onClose={onClose} />
    );
  }

  if (channelId == null) {
    return <ContractPickerBody onClose={onClose} />;
  }

  return (
    <Suspense fallback={<ContractFormLoadingBody onClose={onClose} />}>
      <ContractFormBody
        key={channelId}
        channelId={channelId}
        onSubmitSuccess={onSubmitSuccess}
        onClose={onClose}
      />
    </Suspense>
  );
}

function ContractScrollArea({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-0 flex-1 flex-col items-center gap-4 self-stretch overflow-y-auto p-4">
      {children}
    </div>
  );
}

function ContractSuccessBody({
  submitSuccess,
  onClose,
}: {
  submitSuccess: ContractSubmitSuccess;
  onClose: () => void;
}) {
  return (
    <>
      <ContractScrollArea>
        <Success
          name={submitSuccess.name}
          logoSquareUrl={submitSuccess.logoSquareUrl}
          logoRectangleUrl={submitSuccess.logoRectangleUrl}
        />
      </ContractScrollArea>

      <ContractFormFooter variant="success" onClose={onClose} />
    </>
  );
}

function ContractPickerBody({ onClose }: { onClose: () => void }) {
  const { channelId, channelName, setSelectedChannel } =
    useAddContractContext();

  return (
    <>
      <ContractScrollArea>
        <ChannelSearchSection
          channelId={channelId}
          channelName={channelName}
          onChannelChange={setSelectedChannel}
        />
      </ContractScrollArea>

      <ContractFormFooter variant="disabled" onClose={onClose} />
    </>
  );
}

function ContractFormLoadingBody({ onClose }: { onClose: () => void }) {
  const { channelId, channelName, setSelectedChannel } =
    useAddContractContext();

  return (
    <>
      <ContractScrollArea>
        <ChannelSearchSection
          channelId={channelId}
          channelName={channelName}
          onChannelChange={setSelectedChannel}
        />

        <Separator className="max-w-[500px]" />

        <ChannelDetails.Skeleton />
      </ContractScrollArea>

      <ContractFormFooter variant="disabled" onClose={onClose} />
    </>
  );
}

function ContractFormBody({
  channelId,
  onSubmitSuccess,
  onClose,
}: {
  channelId: number;
  onSubmitSuccess: (result: ContractSubmitSuccess) => void;
  onClose: () => void;
}) {
  const { channelName, setSelectedChannel } = useAddContractContext();
  const { hookForm, channelDetails, handleFormSubmit, isSubmitting } =
    useContractForm({ channelId, onSubmitSuccess });

  return (
    <>
      <form
        id="add-contract-form"
        noValidate
        className="flex min-h-0 flex-1 flex-col gap-0"
        onSubmit={e => void handleFormSubmit(e)}
      >
        <ContractScrollArea>
          <ChannelSearchSection
            channelId={channelId}
            channelName={channelName}
            onChannelChange={setSelectedChannel}
          />

          <Separator className="max-w-[500px]" />

          <ChannelDetails
            channelId={channelId}
            channelDetails={channelDetails}
            hookForm={hookForm}
          />
        </ContractScrollArea>
      </form>

      <ContractFormFooter
        variant="submit"
        onClose={onClose}
        isSubmitting={isSubmitting}
        hookForm={hookForm}
      />
    </>
  );
}
